import * as React from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import {
  CheckSquare,
  Coffee,
  Download,
  Loader2,
  Mail,
  MessageSquare,
  Receipt,
  Search,
  Settings,
  Sparkles,
  Trash2,
  UserRoundPlus,
} from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableFloatingBar } from "@/components/data-table/data-table-floating-bar";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { useUserProfile } from "@/hooks/useUserProfile";
import { api } from "@/lib/convexApi";
import { toUserFacingErrorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";
import {
  formatCurrency,
  formatRelativeTime,
  formatUtcDateOnly,
} from "@/lib/format";
import {
  canPermanentlyDeleteAccountingBills,
  hasPermission,
} from "@/lib/roles";
import {
  BILL_FILTER_ALL,
  BILL_STATUS_LABELS,
  BillStatusBadge,
  OpenQuestionFlagBadge,
  hasActiveBillListFilters,
  internalProfileLabel,
  parseBillListFilters,
  type BillStatus,
  type InternalProfileOption,
} from "./bill-shared";
import { ExportBillsDialog } from "./export-dialog";
import { NewBillDialog } from "./new-bill-dialog";

const ALL = BILL_FILTER_ALL;
const UNASSIGNED = "__unassigned__";
const NO_VENDOR = "__no_vendor__";

type BillStatusAction = "submit" | "approve";

interface BillStatusChangeRequest {
  action: BillStatusAction;
  billIds: string[];
  billCount: number;
  source: "row" | "bulk";
  clearSelection?: () => void;
}

/** Server cap requested per page load; the table paginates client-side. */
const LIST_LIMIT = 500;

const STATUS_TABS: { value: string; label: string; countKey?: string }[] = [
  { value: ALL, label: "All" },
  { value: "needs_review", label: "Needs review", countKey: "needs_review" },
  {
    value: "pending_approval",
    label: "Pending approval",
    countKey: "pending_approval",
  },
  { value: "approved", label: "Approved", countKey: "approved" },
  { value: "exported", label: "Exported", countKey: "exported" },
  { value: "paid", label: "Paid", countKey: "paid" },
];

// Pseudo-views: NOT real bill.status values. They cross statuses and are
// driven by dedicated listBills booleans + getBillCounts keys, so they clear
// the status filter when selected (handled via the `view` URL param).
const VIEW_TABS: { value: string; label: string; countKey: string }[] = [
  {
    value: "awaiting_payment",
    label: "Awaiting payment",
    countKey: "awaitingPayment",
  },
  {
    value: "export_problems",
    label: "Export problems",
    countKey: "exportProblems",
  },
];

interface BillListRow {
  _id: string;
  status: string;
  source: string;
  invoiceNumber?: string;
  vendorId?: string;
  vendorName?: string;
  invoiceDate?: number;
  dueDate?: number;
  totalAmount?: number;
  assigneeProfileId?: string;
  viewerCanApproveAssignedReview?: boolean;
  viewerCanReassign?: boolean;
  duplicateState?: string;
  extractionStatus?: string;
  revision: number;
  overdue: boolean;
  createdAt: number;
  openQuestionAt?: number;
  lastCommentBody?: string;
  lastCommentActorLabel?: string;
  lastCommentAt?: number;
  commentCount?: number;
}

interface BillCommentRow {
  _id: string;
  kind: string;
  actorLabel?: string;
  message?: string;
  createdAt: number;
}

interface InlineBillSelectOption {
  value: string;
  label: string;
}

function InlineBillSelectCell({
  value,
  placeholder,
  ariaLabel,
  options,
  disabled,
  loading,
  className,
  successMessage,
  errorMessage,
  onUpdate,
}: {
  value: string;
  placeholder: string;
  ariaLabel: string;
  options: InlineBillSelectOption[];
  disabled: boolean;
  loading: boolean;
  className?: string;
  successMessage: string;
  errorMessage: string;
  onUpdate: (value: string) => Promise<unknown>;
}) {
  const [displayValue, setDisplayValue] = React.useState(value);
  const [isSaving, setIsSaving] = React.useState(false);
  const latestServerValueRef = React.useRef(value);

  React.useEffect(() => {
    latestServerValueRef.current = value;
    setDisplayValue(value);
  }, [value]);

  const handleValueChange = async (nextValue: string) => {
    if (nextValue === displayValue) return;
    setDisplayValue(nextValue);
    setIsSaving(true);
    try {
      await onUpdate(nextValue);
      toast.success(successMessage);
    } catch (error) {
      setDisplayValue(latestServerValueRef.current);
      toast.error(toUserFacingErrorMessage(error, errorMessage));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div data-no-row-click onClick={(event) => event.stopPropagation()}>
      <Select
        value={displayValue}
        disabled={disabled || loading || isSaving}
        onValueChange={(nextValue) => void handleValueChange(nextValue)}
      >
        <SelectTrigger
          aria-label={ariaLabel}
          className={cn(
            "h-8 border-transparent bg-transparent px-2 shadow-none hover:bg-muted",
            className,
          )}
        >
          <SelectValue placeholder={loading ? "Loading…" : placeholder} />
          {isSaving && (
            <Loader2
              aria-hidden="true"
              className="text-muted-foreground ml-auto h-3.5 w-3.5 animate-spin"
            />
          )}
        </SelectTrigger>
        <SelectContent onClick={(event) => event.stopPropagation()}>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function InlineBillStatusCell({
  bill,
  billLabel,
  actionId,
  action,
  disabled,
  onRequest,
}: {
  bill: BillListRow;
  billLabel: string;
  actionId: string;
  action: BillStatusAction;
  disabled: boolean;
  onRequest: (
    action: BillStatusAction,
    trigger: HTMLButtonElement | null,
  ) => void;
}) {
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const visibleLabel = action === "approve" ? "Approve" : "Submit";
  const accessibleLabel =
    action === "approve"
      ? `Approve ${billLabel}`
      : `Submit for approval — ${billLabel}`;

  return (
    <div
      data-no-row-click
      className="flex min-w-max items-center gap-2"
      onClick={(event) => event.stopPropagation()}
    >
      <BillStatusBadge status={bill.status} />
      <Button
        id={actionId}
        ref={triggerRef}
        type="button"
        variant="outline"
        size="sm"
        className="h-7 px-2 text-xs"
        aria-label={accessibleLabel}
        disabled={disabled}
        onClick={() => onRequest(action, triggerRef.current)}
      >
        {visibleLabel}
      </Button>
    </div>
  );
}

const COMMENT_KIND_LABELS: Record<string, string> = {
  question: "asked",
  question_answered: "answered",
};

/**
 * Latest-comment snippet with a click-to-open popover holding the full
 * discussion. The history query only runs while the popover is open, so the
 * list never pays a per-row events read.
 */
function BillCommentsCell({
  authUserId,
  bill,
}: {
  authUserId: string | null | undefined;
  bill: BillListRow;
}) {
  const [open, setOpen] = React.useState(false);
  const comments = useQuery(
    api.accountingBills.listBillComments,
    open && authUserId ? { authUserId, billId: bill._id } : "skip",
  ) as BillCommentRow[] | undefined;

  if (!bill.commentCount) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="hover:bg-accent flex w-full min-w-0 items-center gap-1.5 rounded-sm px-1 py-0.5 text-left"
          onClick={(e) => e.stopPropagation()}
          aria-label={`Read ${bill.commentCount} comment${bill.commentCount === 1 ? "" : "s"}`}
        >
          <MessageSquare className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
          <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
            {bill.commentCount}
          </span>
          <span className="min-w-0 truncate text-xs">
            {bill.lastCommentActorLabel && (
              <span className="text-muted-foreground">
                {bill.lastCommentActorLabel}:{" "}
              </span>
            )}
            {bill.lastCommentBody}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-96 p-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b px-3 py-2 text-sm font-medium">
          Comments
          {bill.invoiceNumber ? ` — ${bill.invoiceNumber}` : ""}
        </div>
        <div className="max-h-80 space-y-2 overflow-y-auto p-3">
          {comments === undefined ? (
            <p className="text-muted-foreground text-sm">Loading…</p>
          ) : comments.length === 0 ? (
            <p className="text-muted-foreground text-sm">No comments.</p>
          ) : (
            comments.map((comment) => (
              <div key={comment._id} className="text-sm">
                <p className="text-muted-foreground text-xs">
                  {comment.actorLabel ?? "Unknown"}
                  {COMMENT_KIND_LABELS[comment.kind]
                    ? ` ${COMMENT_KIND_LABELS[comment.kind]}`
                    : ""}{" "}
                  · {formatRelativeTime(comment.createdAt)}
                </p>
                <p className="whitespace-pre-wrap break-words">
                  {comment.message}
                </p>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface IntakeExceptionRow {
  _id: string;
  fromAddress: string;
  subject?: string;
  receivedAt: number;
  attachmentCount: number;
  bodyText?: string;
  detectedLinks?: string[];
  attachmentMetadata?: Array<{
    filename: string;
    contentType?: string;
    fileSize: number;
    transferStatus:
      | "included"
      | "empty"
      | "too_large"
      | "too_many"
      | "missing_content";
  }>;
  status: string;
  exceptionKind?: string;
  errorMessage?: string;
  billId?: string;
  createdAt: number;
  convertibleAttachments?: Array<{
    _id: string;
    filename: string;
    fileSize: number;
    conversionStatus: "ready" | "converted" | "failed";
    convertedDocumentId?: string;
    conversionError?: string;
  }>;
}

const INTAKE_EXCEPTION_LABELS: Record<string, string> = {
  no_supported_attachment: "No usable attachment",
  no_attachment: "No file attachment",
  unsupported_attachment: "Unsupported attachment type",
  attachment_too_large: "Attachment exceeds 15 MB",
  too_many_attachments: "Email has more than 10 attachments",
  store_failed: "Attachment could not be stored",
  processing_failed: "Attachment processing could not finish",
  create_failed: "Bill could not be created",
  duplicate_message: "Duplicate delivery",
};

function getIntakeExceptionLabel(row: IntakeExceptionRow): string {
  if (
    row.convertibleAttachments?.some(
      (attachment) => attachment.conversionStatus !== "converted",
    )
  ) {
    return "Bill created; a companion spreadsheet is ready to convert";
  }
  if (
    row.convertibleAttachments?.some(
      (attachment) => attachment.conversionStatus === "converted",
    )
  ) {
    return "Bill created; the companion spreadsheet is attached as PDF";
  }
  if (row.billId && row.exceptionKind === "unsupported_attachment") {
    return "Bill created; a companion attachment was skipped";
  }
  if (row.billId && row.exceptionKind === "attachment_too_large") {
    return "Bill created; an oversized companion attachment was skipped";
  }
  if (row.billId && row.exceptionKind === "too_many_attachments") {
    return "Bill created; extra companion attachments were skipped";
  }
  return row.exceptionKind
    ? (INTAKE_EXCEPTION_LABELS[row.exceptionKind] ?? row.exceptionKind)
    : row.status === "claimed"
      ? "Stuck mid-processing"
      : row.status;
}

function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatAttachmentTransferStatus(
  status: NonNullable<
    IntakeExceptionRow["attachmentMetadata"]
  >[number]["transferStatus"],
): string {
  switch (status) {
    case "too_large":
      return "too large";
    case "too_many":
      return "too many attachments";
    case "empty":
      return "empty";
    case "missing_content":
      return "content unavailable";
    case "included":
      return "";
  }
}

function safeIntakeLink(value: string): { href: string; hostname: string } | null {
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    return { href: url.toString(), hostname: url.hostname };
  } catch {
    return null;
  }
}

function IntakeExceptionsButton({
  exceptions,
  listSearch,
  authUserId,
  canEdit,
}: {
  exceptions: IntakeExceptionRow[] | undefined;
  listSearch: string;
  authUserId: string | null | undefined;
  canEdit: boolean;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const count = exceptions?.length ?? 0;
  const dismissException = useMutation(
    (api as any).accountingBillIntake.dismissIntakeException,
  );
  const promoteException = useMutation(
    api.accountingBills.promoteIntakeExceptionToBill,
  );
  const getSpreadsheetSource = useAction(
    api.accountingBillDocuments.getIntakeSpreadsheetSourceUrl,
  );
  const attachConvertedSpreadsheet = useAction(
    api.accountingBillDocuments.attachConvertedIntakeSpreadsheet,
  );
  const [dismissingId, setDismissingId] = React.useState<string | null>(null);
  const [promotingId, setPromotingId] = React.useState<string | null>(null);
  const [convertingAttachmentId, setConvertingAttachmentId] =
    React.useState<string | null>(null);

  const handleDismiss = async (messageId: string) => {
    if (!authUserId) return;
    setDismissingId(messageId);
    try {
      await dismissException({ authUserId, messageId });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not dismiss the row",
      );
    } finally {
      setDismissingId(null);
    }
  };

  const handlePromote = async (messageId: string) => {
    if (!authUserId) return;
    setPromotingId(messageId);
    try {
      const result = await promoteException({ authUserId, messageId });
      toast.success(
        result.created ? "Bill created in Needs review" : "Bill already exists",
      );
      setOpen(false);
      // A new bill has no vendor or assignee yet. Start its review queue
      // without filters that could hide it when returning to the list.
      const destinationSearch = result.created
        ? "?status=needs_review"
        : listSearch;
      navigate(`/admin/accounting/bills/${result.billId}${destinationSearch}`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not create the bill",
      );
    } finally {
      setPromotingId(null);
    }
  };

  const handleConvertSpreadsheet = async (attachmentId: string) => {
    setConvertingAttachmentId(attachmentId);
    try {
      const source = await getSpreadsheetSource({ attachmentId });
      if (source.converted) {
        toast.success("Spreadsheet is already attached as a PDF");
        return;
      }
      let response: Response;
      try {
        response = await fetch(source.url);
      } catch {
        throw new Error(
          "Could not download the private spreadsheet. Check your connection and try again.",
        );
      }
      if (!response.ok) {
        throw new Error(
          "Could not download the private spreadsheet. Try again in a moment.",
        );
      }
      const { convertSpreadsheetToPdf } = await import("./spreadsheet-to-pdf");
      const converted = await convertSpreadsheetToPdf(
        await response.arrayBuffer(),
        source.filename,
      );
      const result = await attachConvertedSpreadsheet({
        attachmentId,
        fileData: converted.fileData,
      });
      toast.success(
        result.created
          ? `${converted.sheetCount} sheet${converted.sheetCount === 1 ? "" : "s"} converted and attached`
          : "Spreadsheet is already attached as a PDF",
      );
    } catch (error) {
      toast.error(
        toUserFacingErrorMessage(error, "Could not convert the spreadsheet"),
      );
    } finally {
      setConvertingAttachmentId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Mail className="mr-2 h-4 w-4" />
          Intake
          {count > 0 && (
            <Badge className="ml-2" variant="destructive">
              {count}
            </Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Email intake exceptions</DialogTitle>
          <DialogDescription>
            Create a bill to move an email into Needs review. The bill opens
            so you can attach the invoice and complete its details.
          </DialogDescription>
        </DialogHeader>
        {exceptions === undefined ? (
          <p role="status" className="text-muted-foreground text-sm">
            Loading intake exceptions…
          </p>
        ) : count === 0 ? (
          <p className="text-muted-foreground text-sm">
            No intake exceptions to resolve.
          </p>
        ) : (
          <div className="max-h-96 space-y-1 overflow-y-auto">
            {(exceptions ?? []).map((row) => (
              <div
                key={row._id}
                className="rounded-md border px-3 py-2 text-sm"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate font-medium">
                    {row.fromAddress}
                  </span>
                  <span className="text-muted-foreground shrink-0 text-xs">
                    {formatUtcDateOnly(row.receivedAt, { month: "short" })}
                  </span>
                </div>
                {row.subject && (
                  <p className="text-muted-foreground truncate text-xs">
                    “{row.subject}”
                  </p>
                )}
                <p className="text-destructive text-xs">
                  {getIntakeExceptionLabel(row)}
                  {row.errorMessage ? ` — ${row.errorMessage}` : ""}
                </p>
                {row.attachmentMetadata && row.attachmentMetadata.length > 0 && (
                  <ul className="text-muted-foreground mt-1 space-y-0.5 text-xs">
                    {row.attachmentMetadata.map((attachment, index) => (
                      <li key={`${attachment.filename}-${index}`}>
                        {attachment.filename} ·{" "}
                        {formatAttachmentSize(attachment.fileSize)}
                        {attachment.contentType
                          ? ` · ${attachment.contentType}`
                          : ""}
                        {formatAttachmentTransferStatus(
                          attachment.transferStatus,
                        )
                          ? ` · ${formatAttachmentTransferStatus(attachment.transferStatus)}`
                          : ""}
                      </li>
                    ))}
                  </ul>
                )}
                {row.convertibleAttachments?.map((attachment) => (
                  <div
                    key={attachment._id}
                    className="bg-muted/50 mt-2 flex items-center justify-between gap-3 rounded-md px-2 py-1.5"
                  >
                    <span className="min-w-0 truncate text-xs">
                      {attachment.filename}
                    </span>
                    {attachment.conversionStatus === "converted" ? (
                      <span className="text-muted-foreground shrink-0 text-xs">
                        Attached as PDF
                      </span>
                    ) : canEdit ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 shrink-0 text-xs"
                        disabled={
                          convertingAttachmentId !== null ||
                          dismissingId === row._id ||
                          promotingId === row._id
                        }
                        onClick={() =>
                          void handleConvertSpreadsheet(attachment._id)
                        }
                      >
                        {convertingAttachmentId === attachment._id ? (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        ) : null}
                        {convertingAttachmentId === attachment._id
                          ? "Converting…"
                          : "Convert to PDF & attach"}
                      </Button>
                    ) : null}
                  </div>
                ))}
                {row.billId &&
                  (!row.convertibleAttachments ||
                    row.convertibleAttachments.length === 0) &&
                  row.attachmentMetadata?.some((attachment) =>
                    /\.(xlsx|xlsm)$/i.test(attachment.filename),
                  ) && (
                    <p className="text-muted-foreground mt-1 text-xs">
                      This older spreadsheet was not retained. An administrator
                      can recover it from the original email.
                    </p>
                  )}
                {row.bodyText && (
                  <details className="text-muted-foreground mt-1 text-xs">
                    <summary className="cursor-pointer font-medium">
                      View retained email message
                    </summary>
                    <p className="mt-1 max-h-48 overflow-y-auto whitespace-pre-wrap break-words">
                      {row.bodyText}
                    </p>
                  </details>
                )}
                {row.detectedLinks && row.detectedLinks.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    {row.detectedLinks.map((value) => {
                      const link = safeIntakeLink(value);
                      return link ? (
                        <a
                          key={link.href}
                          href={link.href}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs underline underline-offset-2"
                        >
                          Open {link.hostname}
                        </a>
                      ) : null;
                    })}
                  </div>
                )}
                <div className="mt-1 flex items-center gap-3">
                  {row.billId && (
                    <button
                      type="button"
                      className="text-xs underline underline-offset-2"
                      onClick={() => {
                        setOpen(false);
                        navigate(
                          `/admin/accounting/bills/${row.billId}${listSearch}`,
                        );
                      }}
                    >
                      Open the bill and stored document
                    </button>
                  )}
                  {canEdit && !row.billId && (
                    <button
                      type="button"
                      className="text-xs font-medium underline underline-offset-2 disabled:opacity-50"
                      disabled={
                        promotingId === row._id ||
                        dismissingId === row._id ||
                        convertingAttachmentId !== null
                      }
                      onClick={() => void handlePromote(row._id)}
                    >
                      {promotingId === row._id
                        ? "Creating…"
                        : "Create bill for review"}
                    </button>
                  )}
                  {canEdit && (
                    <button
                      type="button"
                      className="text-muted-foreground text-xs underline underline-offset-2 disabled:opacity-50"
                      disabled={
                        dismissingId === row._id ||
                        promotingId === row._id ||
                        convertingAttachmentId !== null
                      }
                      onClick={() => void handleDismiss(row._id)}
                    >
                      {dismissingId === row._id ? "Dismissing…" : "Dismiss"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ApprovalQueueRow {
  billId: string;
  invoiceNumber?: string;
  payee?: string;
  totalAmount?: number;
  dueDate?: number;
  stepIndex: number;
  policyName: string;
  submittedAt: number;
}

function ApprovalQueueButton({
  queue,
  listSearch,
}: {
  queue: ApprovalQueueRow[] | undefined;
  listSearch: string;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const count = queue?.length ?? 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <CheckSquare className="mr-2 h-4 w-4" />
          Approvals
          {count > 0 && (
            <Badge className="ml-2" variant="destructive">
              {count}
            </Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Bills waiting on you</DialogTitle>
          <DialogDescription>
            Bills where you are an approver for the current step.
          </DialogDescription>
        </DialogHeader>
        {count === 0 ? (
          <p className="text-muted-foreground text-sm">
            Nothing waiting on your approval.
          </p>
        ) : (
          <div className="max-h-80 space-y-1 overflow-y-auto">
            {(queue ?? []).map((row) => (
              <button
                key={row.billId}
                type="button"
                className="hover:bg-accent flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm"
                onClick={() => {
                  setOpen(false);
                  navigate(`/admin/accounting/bills/${row.billId}${listSearch}`);
                }}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">
                    {row.invoiceNumber ?? "(no invoice #)"}
                    {row.payee ? ` — ${row.payee}` : ""}
                  </span>
                  <span className="text-muted-foreground block text-xs">
                    {row.policyName} · step {row.stepIndex + 1}
                    {row.dueDate !== undefined
                      ? ` · due ${formatUtcDateOnly(row.dueDate, { month: "short" })}`
                      : ""}
                  </span>
                </span>
                <span className="tabular-nums">
                  {row.totalAmount !== undefined
                    ? formatCurrency(row.totalAmount)
                    : "—"}
                </span>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AccountingBillsPage() {
  const { authUserId, profile, user } = useUserProfile();
  const { userRole } = useAuthIdentity();
  // getMyProfile returns the userProfiles doc, so profile._id is this user's
  // Id<'userProfiles'> — exactly what listBills.forAttentionProfileId expects.
  const myProfileId = (profile?._id as string | undefined) ?? undefined;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const canEdit = hasPermission(userRole, "accounting:edit");
  const canApprove = hasPermission(userRole, "accounting:approve");
  const canManage = hasPermission(userRole, "accounting:manage");
  const canDeleteBills = canPermanentlyDeleteAccountingBills(
    userRole,
    user?.email as string | undefined,
  );

  const statusParam = searchParams.get("status") ?? ALL;
  const viewParam = searchParams.get("view");
  const overdueOnly = searchParams.get("overdue") === "1";
  const attentionOnly = searchParams.get("attention") === "1";
  const vendorParam = searchParams.get("vendor") ?? ALL;
  const assigneeParam = searchParams.get("assignee") ?? ALL;
  const searchParam = searchParams.get("q") ?? "";
  // Carried into detail links so the detail page can rebuild this queue and
  // "back" returns to the same filtered view.
  const listSearch = searchParams.toString() ? `?${searchParams.toString()}` : "";

  const [searchInput, setSearchInput] = React.useState(searchParam);
  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchInput === searchParam) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (searchInput) next.set("q", searchInput);
          else next.delete("q");
          return next;
        },
        { replace: true },
      );
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput, searchParam, setSearchParams]);

  const setParam = (key: string, value: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === null) next.delete(key);
      else next.set(key, value);
      return next;
    });
  };

  const setParams = (updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null) next.delete(key);
        else next.set(key, value);
      }
      return next;
    });
  };

  const counts = useQuery(
    api.accountingBills.getBillCounts,
    authUserId ? { authUserId } : "skip",
  ) as Record<string, number> | undefined;

  const listFilters = React.useMemo(
    () => parseBillListFilters(searchParams, myProfileId),
    [searchParams, myProfileId],
  );

  const bills = useQuery(
    api.accountingBills.listBills,
    authUserId ? { authUserId, ...listFilters, limit: LIST_LIMIT } : "skip",
  ) as BillListRow[] | undefined;

  const vendors = useQuery(
    api.accountingBillMasters.listVendors,
    authUserId ? { authUserId, includeInactive: true } : "skip",
  ) as
    | { _id: string; displayName: string; active: boolean }[]
    | undefined;

  const internalProfiles = useQuery(
    api.accountingBillMasters.listInternalProfiles,
    authUserId ? { authUserId } : "skip",
  ) as InternalProfileOption[] | undefined;

  const approvalQueue = useQuery(
    api.accountingBills.getApprovalQueue,
    authUserId && canApprove ? { authUserId } : "skip",
  ) as ApprovalQueueRow[] | undefined;

  const approvalQueueBillIds = React.useMemo(
    () => new Set((approvalQueue ?? []).map((row) => row.billId)),
    [approvalQueue],
  );

  const intakeExceptions = useQuery(
    api.accountingBillIntake.listIntakeExceptions,
    authUserId && canEdit ? { authUserId } : "skip",
  ) as IntakeExceptionRow[] | undefined;

  const profileNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const profile of internalProfiles ?? []) {
      map.set(profile._id, internalProfileLabel(profile));
    }
    return map;
  }, [internalProfiles]);

  const assigneeOptions = React.useMemo<InlineBillSelectOption[]>(
    () => [
      { value: UNASSIGNED, label: "Unassigned" },
      ...(internalProfiles ?? []).map((profile) => ({
        value: profile._id,
        label: internalProfileLabel(profile),
      })),
    ],
    [internalProfiles],
  );
  const [exportOpen, setExportOpen] = React.useState(false);
  const [exportSeedIds, setExportSeedIds] = React.useState<string[]>([]);
  const updateBill = useMutation(api.accountingBills.updateBill);
  const assignBill = useMutation(api.accountingBills.assignBill);
  const bulkAssignBills = useMutation(api.accountingBills.bulkAssignBills);
  const [isBulkAssigning, setIsBulkAssigning] = React.useState(false);
  const bulkChangeBillStatus = useMutation(
    api.accountingBills.bulkChangeBillStatus,
  );
  const [statusChangeRequest, setStatusChangeRequest] =
    React.useState<BillStatusChangeRequest | null>(null);
  const [isStatusUpdating, setIsStatusUpdating] = React.useState(false);
  const bulkStatusTriggerRef = React.useRef<HTMLButtonElement>(null);
  const statusReturnFocusRef = React.useRef<HTMLButtonElement | null>(null);
  const statusReturnFocusIdRef = React.useRef<string | null>(null);
  const statusChangeCompletedRef = React.useRef(false);
  const pageHeadingRef = React.useRef<HTMLHeadingElement>(null);
  const deleteBills = useAction(api.accountingBillDocuments.deleteBills);
  const [deleteRequest, setDeleteRequest] = React.useState<{
    billIds: string[];
    labels: string[];
    clearSelection: () => void;
  } | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleDeleteSelected = async () => {
    if (!deleteRequest) return;
    setIsDeleting(true);
    try {
      const result = await deleteBills({
        billIds: deleteRequest.billIds,
        acknowledgement: "PERMANENTLY_DELETE_BILLS",
      });
      toast.success(
        `Deleted ${result.deletedBillIds.length} bill${result.deletedBillIds.length === 1 ? "" : "s"}`,
      );
      if (result.orphanedR2Objects > 0) {
        toast.error("The bill records were deleted, but some stored files need cleanup.");
      }
      deleteRequest.clearSelection();
      setDeleteRequest(null);
    } catch (error) {
      toast.error(
        toUserFacingErrorMessage(
          error,
          "Couldn't delete the selected bills. Refresh and try again.",
        ),
        { description: "No bills were deleted. The selection is unchanged." },
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkAssign = async (
    selectedRows: BillListRow[],
    value: string,
    clearSelection: () => void,
  ) => {
    if (!authUserId || selectedRows.length === 0) return;
    setIsBulkAssigning(true);
    try {
      await bulkAssignBills({
        authUserId,
        billIds: selectedRows.map((row) => row._id),
        assigneeProfileId: value === UNASSIGNED ? null : value,
      });
      const assigneeLabel =
        value === UNASSIGNED
          ? null
          : (profileNameById.get(value) ?? "the selected person");
      toast.success(
        assigneeLabel
          ? `Assigned ${selectedRows.length} bill${selectedRows.length === 1 ? "" : "s"} to ${assigneeLabel}`
          : `Unassigned ${selectedRows.length} bill${selectedRows.length === 1 ? "" : "s"}`,
      );
      clearSelection();
    } catch (error) {
      toast.error(
        toUserFacingErrorMessage(
          error,
          "Couldn't update the selected bills. Refresh and try again.",
        ),
        { description: "The selected bills remain selected." },
      );
    } finally {
      setIsBulkAssigning(false);
    }
  };

  const handleStatusChange = async () => {
    if (!authUserId || !statusChangeRequest) return;
    setIsStatusUpdating(true);
    try {
      const result = await bulkChangeBillStatus({
        authUserId,
        billIds: statusChangeRequest.billIds,
        action: statusChangeRequest.action,
      });
      if (statusChangeRequest.action === "submit") {
        toast.success(
          `Submitted ${result.updatedCount} bill${result.updatedCount === 1 ? "" : "s"} for approval`,
        );
      } else {
        const detail =
          statusChangeRequest.source === "row"
            ? result.advancedCount > 0
              ? "The bill moved to its next approval step."
              : "The bill completed its approval route."
            : result.advancedCount > 0
              ? `${result.fullyApprovedCount} fully approved; ${result.advancedCount} moved to the next approval step.`
              : "All selected bills completed their approval route.";
        toast.success(
          `Approved the current step for ${result.updatedCount} bill${result.updatedCount === 1 ? "" : "s"}`,
          { description: detail },
        );
      }
      statusChangeRequest.clearSelection?.();
      statusChangeCompletedRef.current = true;
      setStatusChangeRequest(null);
    } catch (error) {
      toast.error(
        toUserFacingErrorMessage(
          error,
          statusChangeRequest.source === "bulk"
            ? "Couldn't update the selected bills. Refresh and try again."
            : "Couldn't update this bill. Refresh and try again.",
        ),
        {
          description:
            statusChangeRequest.source === "bulk"
              ? "No bills were changed. The selected bills remain selected."
              : "The bill was not changed.",
        },
      );
    } finally {
      setIsStatusUpdating(false);
    }
  };

  const profilesLoading = internalProfiles === undefined;
  const vendorsLoading = vendors === undefined;

  const columns = React.useMemo<ColumnDef<BillListRow>[]>(
    () => [
      {
        accessorKey: "invoiceNumber",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Invoice #" />
        ),
        cell: ({ row }) => (
          <span className="flex items-center gap-1.5 font-medium">
            {/* Real link so middle-click / Cmd-click open-in-tab works. */}
            <Link
              to={`/admin/accounting/bills/${row.original._id}${listSearch}`}
              className="hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.invoiceNumber ?? (
                <span className="text-muted-foreground">(no #)</span>
              )}
            </Link>
            {row.original.duplicateState === "suspected" && (
              <Badge
                variant="secondary"
                className="h-4 shrink-0 rounded px-1 text-[10px] font-medium leading-none"
              >
                possible duplicate
              </Badge>
            )}
            {row.original.openQuestionAt !== undefined && (
              <OpenQuestionFlagBadge />
            )}
            {row.original.source === "email" && (
              <span title="Received by email" aria-label="Received by email">
                <Mail className="text-muted-foreground h-3 w-3" />
              </span>
            )}
            {(row.original.extractionStatus === "processing" ||
              row.original.extractionStatus === "queued") && (
              <span title="Reading the document…" aria-label="AI reading document">
                <Loader2 className="text-muted-foreground h-3 w-3 animate-spin" />
              </span>
            )}
            {row.original.extractionStatus === "succeeded" && (
              <span
                title="AI suggestions ready"
                aria-label="AI suggestions ready"
              >
                <Sparkles className="h-3 w-3 text-violet-500 dark:text-violet-400" />
              </span>
            )}
            {row.original.extractionStatus === "failed" && (
              <span
                title="AI could not read the document — open to re-run"
                aria-label="AI extraction failed"
              >
                <Sparkles className="text-destructive/70 h-3 w-3" />
              </span>
            )}
          </span>
        ),
      },
      {
        accessorKey: "vendorName",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Vendor" />
        ),
        cell: ({ row }) => {
          const bill = row.original;
          const billLabel = bill.invoiceNumber
            ? `bill ${bill.invoiceNumber}`
            : `bill row ${row.index + 1} without an invoice number`;
          const canEditVendor =
            canEdit && ["draft", "needs_review"].includes(bill.status);
          if (!canEditVendor) {
            return (
              bill.vendorName ?? (
                <span className="text-muted-foreground">—</span>
              )
            );
          }
          return (
            <InlineBillSelectCell
              value={bill.vendorId ?? ""}
              placeholder={bill.vendorName ?? "Select vendor"}
              ariaLabel={`Change vendor for ${billLabel}`}
              options={[
                { value: NO_VENDOR, label: "No vendor" },
                ...(vendors ?? [])
                  .filter(
                    (vendor) => vendor.active || vendor._id === bill.vendorId,
                  )
                  .map((vendor) => ({
                    value: vendor._id,
                    label: `${vendor.displayName}${vendor.active ? "" : " (inactive)"}`,
                  })),
              ]}
              disabled={!authUserId}
              loading={vendorsLoading}
              className="w-56"
              successMessage="Vendor updated"
              errorMessage="Couldn't update the vendor. Refresh and try again."
              onUpdate={(value) =>
                updateBill({
                  authUserId,
                  billId: bill._id,
                  vendorId: value === NO_VENDOR ? null : value,
                })
              }
            />
          );
        },
      },
      {
        id: "invoiceDate",
        accessorFn: (row) => row.invoiceDate ?? 0,
        size: 110,
        minSize: 90,
        maxSize: 130,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Invoice date" />
        ),
        cell: ({ row }) =>
          row.original.invoiceDate !== undefined
            ? formatUtcDateOnly(row.original.invoiceDate, { month: "short" })
            : "—",
      },
      {
        id: "dueDate",
        // Undated bills sink to the bottom of the due-date-ascending queue.
        accessorFn: (row) => row.dueDate ?? Number.MAX_SAFE_INTEGER,
        size: 110,
        minSize: 90,
        maxSize: 130,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Due date" />
        ),
        cell: ({ row }) => (
          <span
            className={cn(
              row.original.overdue && "text-destructive font-medium",
            )}
          >
            {row.original.dueDate !== undefined
              ? formatUtcDateOnly(row.original.dueDate, { month: "short" })
              : "—"}
            {row.original.overdue && (
              <span className="ml-1 text-xs">(overdue)</span>
            )}
          </span>
        ),
      },
      {
        id: "totalAmount",
        accessorFn: (row) => row.totalAmount ?? 0,
        size: 110,
        minSize: 90,
        maxSize: 130,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label="Amount"
            className="justify-end"
          />
        ),
        cell: ({ row }) => (
          <span className="block text-right tabular-nums">
            {row.original.totalAmount !== undefined
              ? formatCurrency(row.original.totalAmount)
              : "—"}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Status" />
        ),
        cell: ({ row }) => {
          const bill = row.original;
          const billLabel = bill.invoiceNumber
            ? `bill ${bill.invoiceNumber}, row ${row.index + 1}`
            : `bill row ${row.index + 1} without an invoice number`;
          const canApproveAssignedReview =
            bill.viewerCanApproveAssignedReview === true;
          const canSubmit =
            canEdit &&
            bill.status === "needs_review" &&
            !canApproveAssignedReview;
          const canApproveBill =
            (canApprove &&
              bill.status === "pending_approval" &&
              approvalQueueBillIds.has(bill._id)) ||
            canApproveAssignedReview;

          if (!canSubmit && !canApproveBill) {
            return <BillStatusBadge status={bill.status} />;
          }

          return (
            <InlineBillStatusCell
              bill={bill}
              billLabel={billLabel}
              actionId={`bill-status-${canApproveBill ? "approve" : "submit"}-${bill._id}`}
              action={canApproveBill ? "approve" : "submit"}
              disabled={!authUserId || isStatusUpdating}
              onRequest={(action, trigger) => {
                statusReturnFocusRef.current = trigger;
                statusReturnFocusIdRef.current = trigger?.id ?? null;
                statusChangeCompletedRef.current = false;
                setStatusChangeRequest({
                  action,
                  billIds: [bill._id],
                  billCount: 1,
                  source: "row",
                });
              }}
            />
          );
        },
      },
      {
        id: "assignee",
        accessorFn: (row) =>
          row.assigneeProfileId
            ? (profileNameById.get(row.assigneeProfileId) ?? "")
            : "",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Assignee" />
        ),
        cell: ({ row }) => {
          const bill = row.original;
          const billLabel = bill.invoiceNumber
            ? `bill ${bill.invoiceNumber}`
            : `bill row ${row.index + 1} without an invoice number`;
          const canEditAssignee =
            (canEdit && ["draft", "needs_review"].includes(bill.status)) ||
            bill.viewerCanReassign === true;
          if (!canEditAssignee) {
            return bill.assigneeProfileId
              ? profilesLoading
                ? "…"
                : (profileNameById.get(bill.assigneeProfileId) ?? "Unknown")
              : "—";
          }
          return (
            <InlineBillSelectCell
              value={bill.assigneeProfileId ?? UNASSIGNED}
              placeholder="Unassigned"
              ariaLabel={`Change assignee for ${billLabel}`}
              options={assigneeOptions}
              disabled={!authUserId}
              loading={profilesLoading}
              className="w-48"
              successMessage="Assignee updated"
              errorMessage="Couldn't update the assignee. Refresh and try again."
              onUpdate={(value) =>
                assignBill({
                  authUserId,
                  billId: bill._id,
                  assigneeProfileId:
                    value === UNASSIGNED ? null : value,
                })
              }
            />
          );
        },
      },
      {
        id: "comments",
        accessorFn: (row) => row.lastCommentAt ?? 0,
        size: 260,
        minSize: 180,
        maxSize: 320,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Comments" />
        ),
        cell: ({ row }) => (
          <BillCommentsCell authUserId={authUserId} bill={row.original} />
        ),
      },
    ],
    [
      assigneeOptions,
      assignBill,
      approvalQueueBillIds,
      authUserId,
      canApprove,
      canEdit,
      isStatusUpdating,
      listSearch,
      profileNameById,
      profilesLoading,
      updateBill,
      vendors,
      vendorsLoading,
    ],
  );

  const isLoading = authUserId === undefined || bills === undefined;
  const filtersActive = hasActiveBillListFilters(searchParams);
  const limitReached = (bills?.length ?? 0) >= LIST_LIMIT;
  const totalInView = React.useMemo(
    () =>
      (bills ?? []).reduce((total, bill) => total + (bill.totalAmount ?? 0), 0),
    [bills],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1
            ref={pageHeadingRef}
            tabIndex={-1}
            className="text-3xl font-bold tracking-tight"
          >
            Bills &amp; Expenses
          </h1>
          <p className="text-muted-foreground mt-1">
            Review vendor invoices, code them to GL accounts, route approvals,
            and export to QuickBooks.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" asChild>
            <Link to="/admin/accounting/bills/aci">
              <Coffee className="mr-2 h-4 w-4" />
              ACI costs
            </Link>
          </Button>
          {canEdit && (
            <IntakeExceptionsButton
              exceptions={intakeExceptions}
              listSearch={listSearch}
              authUserId={authUserId}
              canEdit={canEdit}
            />
          )}
          {canApprove && (
            <ApprovalQueueButton queue={approvalQueue} listSearch={listSearch} />
          )}
          {canEdit && (
            <Button
              variant="outline"
              disabled={!authUserId}
              onClick={() => {
                setExportSeedIds([]);
                setExportOpen(true);
              }}
            >
              <Download className="mr-2 h-4 w-4" />
              Export to QBO
            </Button>
          )}
          {canEdit && (
            <NewBillDialog
              authUserId={authUserId ?? undefined}
              vendors={vendors}
              onCreated={(billId) =>
                navigate(`/admin/accounting/bills/${billId}${listSearch}`)
              }
            />
          )}
          {canManage && (
            <Button variant="ghost" size="icon" asChild>
              <Link
                to="/admin/accounting/bills/settings"
                aria-label="Bills settings"
              >
                <Settings className="h-4 w-4" />
              </Link>
            </Button>
          )}
        </div>
      </div>

      <nav className="flex w-fit flex-wrap items-center gap-1 rounded-lg bg-muted p-[3px] text-muted-foreground">
        {STATUS_TABS.map((tab) => {
          // A status tab is never active while a pseudo-view is selected.
          const active = !viewParam && statusParam === tab.value;
          const count = tab.countKey ? counts?.[tab.countKey] : undefined;
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() =>
                setParams({
                  status: tab.value === ALL ? null : tab.value,
                  view: null,
                })
              }
              className={cn(
                "inline-flex items-center justify-center gap-1.5 rounded-md border border-transparent px-3 py-1 text-sm font-medium whitespace-nowrap transition-all",
                active
                  ? "bg-background text-foreground shadow-sm"
                  : "text-foreground/60 hover:text-foreground",
              )}
            >
              {tab.label}
              {count !== undefined && count > 0 && (
                <span className="text-xs tabular-nums opacity-70">{count}</span>
              )}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() =>
            setParams({
              status: !viewParam && statusParam === "voided" ? null : "voided",
              view: null,
            })
          }
          className={cn(
            "inline-flex items-center justify-center gap-1.5 rounded-md border border-transparent px-3 py-1 text-sm font-medium whitespace-nowrap transition-all",
            !viewParam && statusParam === "voided"
              ? "bg-background text-foreground shadow-sm"
              : "text-foreground/60 hover:text-foreground",
          )}
        >
          Voided
        </button>
        <span className="mx-1 h-5 w-px self-center bg-border" aria-hidden />
        {VIEW_TABS.map((tab) => {
          const active = viewParam === tab.value;
          const count = counts?.[tab.countKey];
          return (
            <button
              key={tab.value}
              type="button"
              // Selecting a pseudo-view clears the status filter it overrides.
              onClick={() =>
                setParams({
                  view: active ? null : tab.value,
                  status: null,
                })
              }
              className={cn(
                "inline-flex items-center justify-center gap-1.5 rounded-md border border-transparent px-3 py-1 text-sm font-medium whitespace-nowrap transition-all",
                active
                  ? "bg-background text-foreground shadow-sm"
                  : "text-foreground/60 hover:text-foreground",
              )}
            >
              {tab.label}
              {count !== undefined && count > 0 && (
                <span className="text-xs tabular-nums opacity-70">{count}</span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="text-muted-foreground absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search invoice #, vendor, notes…"
            aria-label="Search bills"
            className="pl-8"
          />
        </div>
        <Select
          value={vendorParam}
          onValueChange={(value) =>
            setParam("vendor", value === ALL ? null : value)
          }
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Vendor" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All vendors</SelectItem>
            {(vendors ?? []).map((vendor) => (
              <SelectItem key={vendor._id} value={vendor._id}>
                {vendor.displayName}
                {vendor.active ? "" : " (inactive)"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={assigneeParam}
          onValueChange={(value) =>
            setParam("assignee", value === ALL ? null : value)
          }
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Assignee" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All assignees</SelectItem>
            {(internalProfiles ?? []).map((profile) => (
              <SelectItem key={profile._id} value={profile._id}>
                {internalProfileLabel(profile)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Boolean filters are checkboxes, not buttons — on/off state is the
            control itself, no styling ambiguity. */}
        <label className="flex h-9 cursor-pointer items-center gap-2 px-1 text-sm">
          <Checkbox
            checked={overdueOnly}
            onCheckedChange={(checked) =>
              setParam("overdue", checked === true ? "1" : null)
            }
          />
          Overdue
          {(counts?.overdue ?? 0) > 0 && (
            <span className="text-destructive text-xs tabular-nums">
              {counts?.overdue}
            </span>
          )}
        </label>
        {myProfileId && (
          <label
            className="flex h-9 cursor-pointer items-center gap-2 px-1 text-sm"
            title="Your assigned bills that are actionable or have an open question"
          >
            <Checkbox
              checked={attentionOnly}
              onCheckedChange={(checked) =>
                setParam("attention", checked === true ? "1" : null)
              }
            />
            For my attention
          </label>
        )}
        <span className="text-muted-foreground ml-auto text-sm">
          {isLoading
            ? "Loading bills…"
            : `${bills?.length ?? 0} ${
                statusParam === ALL
                  ? ""
                  : `${BILL_STATUS_LABELS[statusParam as BillStatus] ?? statusParam} `
              }bill${(bills?.length ?? 0) === 1 ? "" : "s"}${
                overdueOnly ? " overdue" : ""
              }${
                (bills?.length ?? 0) > 0
                  ? ` · ${formatCurrency(totalInView)}`
                  : ""
              }${limitReached ? ` — showing the first ${LIST_LIMIT}, refine filters to see the rest` : ""}`}
        </span>
      </div>

      {isLoading ? (
        <div className="space-y-1.5 rounded-md border p-3">
          <Skeleton className="h-8 w-full" />
          {Array.from({ length: 8 }, (_, index) => (
            <Skeleton key={index} className="h-7 w-full" />
          ))}
        </div>
      ) : bills !== undefined && bills.length === 0 ? (
        filtersActive ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-md border px-8 py-12 text-center">
            <Search className="text-muted-foreground h-8 w-8" />
            <p className="text-foreground font-medium">
              No bills match these filters
            </p>
            <p className="text-muted-foreground text-sm">
              Check the search text or clear the filters to see everything.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => {
                setSearchInput("");
                setSearchParams({}, { replace: true });
              }}
            >
              Clear filters
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 rounded-md border px-8 py-12 text-center">
            <Receipt className="text-muted-foreground h-8 w-8" />
            <p className="text-foreground font-medium">No bills yet</p>
            <p className="text-muted-foreground text-sm">
              Create one or forward an invoice to get started.
            </p>
            {canEdit && (
              <div className="mt-2">
                <NewBillDialog
                  authUserId={authUserId ?? undefined}
                  vendors={vendors}
                  onCreated={(billId) =>
                    navigate(`/admin/accounting/bills/${billId}${listSearch}`)
                  }
                />
              </div>
            )}
          </div>
        )
      ) : (
        <InternalEntityDataTable
          data={bills ?? []}
          columns={columns}
          pageSize={50}
          initialSorting={[{ id: "dueDate", desc: false }]}
          enableSelection={canEdit}
          onRowClick={(row) =>
            navigate(`/admin/accounting/bills/${row.original._id}${listSearch}`)
          }
          getRowClassName={(row) =>
            cn(row.original.overdue && "bg-destructive/5")
          }
          actionBar={({ selectedRows, clearSelection }) => {
            const exportable = selectedRows.filter(
              (row) => row.status === "approved",
            );
            const canDeleteSelection =
              canDeleteBills &&
              selectedRows.length > 0 &&
              selectedRows.every((row) =>
                ["draft", "needs_review", "voided"].includes(row.status),
              );
            const nonAssignable = selectedRows.filter((row) =>
              !["draft", "needs_review"].includes(row.status),
            );
            const canSubmitSelection = selectedRows.every(
              (row) =>
                row.status === "needs_review" &&
                row.viewerCanApproveAssignedReview !== true,
            );
            const canApproveSelection =
              canApprove &&
              approvalQueue !== undefined &&
              selectedRows.every(
                (row) =>
                  (row.status === "pending_approval" &&
                    approvalQueueBillIds.has(row._id)) ||
                  row.viewerCanApproveAssignedReview === true,
              );
            const hasBulkStatusAction =
              canSubmitSelection || canApproveSelection;
            const canAssignSelection = nonAssignable.length === 0;
            const singleSelectedStatus =
              selectedRows.length === 1 ? selectedRows[0]?.status : undefined;
            const bulkActionNotice = canDeleteSelection
              ? null
              : !canAssignSelection && !hasBulkStatusAction
                ? selectedRows.length > 1
                  ? "Select bills with the same available assignment or status action"
                  : singleSelectedStatus === "pending_approval"
                    ? "This bill is awaiting another approver"
                    : singleSelectedStatus === "paid"
                      ? "Paid bills cannot be reassigned or have their status changed"
                      : singleSelectedStatus === "exported"
                        ? "Exported bills cannot be reassigned or have their status changed"
                        : singleSelectedStatus === "approved"
                          ? "This approved bill is ready to export"
                          : "Voided bills cannot be changed from this bulk toolbar"
                : !canAssignSelection
                  ? "Assignment is available only for Draft or Needs review bills"
                  : !hasBulkStatusAction
                    ? "To change status in bulk, select only Needs review bills or only bills awaiting your approval"
                    : null;
            return (
              <DataTableFloatingBar
                selectedCount={selectedRows.length}
                onClear={clearSelection}
              >
                {canAssignSelection && (
                  <Select
                    value=""
                    disabled={profilesLoading || isBulkAssigning || !authUserId}
                    onValueChange={(value) =>
                      void handleBulkAssign(selectedRows, value, clearSelection)
                    }
                  >
                    <SelectTrigger
                      aria-label={`Assign ${selectedRows.length} selected bill${selectedRows.length === 1 ? "" : "s"}`}
                      className="h-8 w-44 bg-background"
                    >
                      {isBulkAssigning ? (
                        <span className="flex items-center gap-1.5">
                          <Loader2
                            aria-hidden="true"
                            className="h-3.5 w-3.5 animate-spin"
                          />
                          Assigning…
                        </span>
                      ) : (
                        <>
                          <UserRoundPlus
                            aria-hidden="true"
                            className="mr-1.5 h-3.5 w-3.5"
                          />
                          <SelectValue placeholder="Assign to…" />
                        </>
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      {assigneeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {hasBulkStatusAction && (
                  <Select
                    value=""
                    disabled={isStatusUpdating || !authUserId}
                    onValueChange={(value) => {
                      statusReturnFocusRef.current =
                        bulkStatusTriggerRef.current;
                      statusChangeCompletedRef.current = false;
                      setStatusChangeRequest({
                        action: value as BillStatusAction,
                        billIds: selectedRows.map((row) => row._id),
                        billCount: selectedRows.length,
                        source: "bulk",
                        clearSelection,
                      });
                    }}
                  >
                    <SelectTrigger
                      ref={bulkStatusTriggerRef}
                      aria-label={`Change status for ${selectedRows.length} selected bill${selectedRows.length === 1 ? "" : "s"}`}
                      className="h-8 w-48 bg-background"
                    >
                      {isStatusUpdating ? (
                        <span className="flex items-center gap-1.5">
                          <Loader2
                            aria-hidden="true"
                            className="h-3.5 w-3.5 animate-spin"
                          />
                          Updating status…
                        </span>
                      ) : (
                        <>
                          <CheckSquare
                            aria-hidden="true"
                            className="mr-1.5 h-3.5 w-3.5"
                          />
                          <SelectValue placeholder="Status…" />
                        </>
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="submit" disabled={!canSubmitSelection}>
                        Submit for approval
                      </SelectItem>
                      <SelectItem value="approve" disabled={!canApproveSelection}>
                        Approve selected
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {bulkActionNotice && (
                  <span
                    role="status"
                    className="text-muted-foreground max-w-64 px-2 text-center text-xs leading-tight sm:max-w-none sm:text-left sm:whitespace-nowrap"
                  >
                    {bulkActionNotice}
                  </span>
                )}
                {exportable.length > 0 && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setExportSeedIds(exportable.map((row) => row._id));
                      setExportOpen(true);
                      clearSelection();
                    }}
                  >
                    <Download className="mr-1.5 h-3.5 w-3.5" />
                    Export {exportable.length} approved to QBO
                  </Button>
                )}
                {canDeleteSelection && (
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={isDeleting}
                    onClick={() =>
                      setDeleteRequest({
                        billIds: selectedRows.map((row) => row._id),
                        labels: selectedRows.map(
                          (row) =>
                            row.invoiceNumber?.trim() ||
                            row.vendorName?.trim() ||
                            "Bill without invoice number",
                        ),
                        clearSelection,
                      })
                    }
                  >
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                    Delete {selectedRows.length}
                  </Button>
                )}
              </DataTableFloatingBar>
            );
          }}
        />
      )}

      {canEdit && (
        <ExportBillsDialog
          authUserId={authUserId ?? undefined}
          open={exportOpen}
          onOpenChange={setExportOpen}
          initialSelectedIds={exportSeedIds}
        />
      )}
      <AlertDialog
        open={statusChangeRequest !== null}
        onOpenChange={(open) => {
          if (isStatusUpdating) return;
          if (!open) setStatusChangeRequest(null);
        }}
      >
        <AlertDialogContent
          onCloseAutoFocus={(event) => {
            if (statusChangeCompletedRef.current) {
              statusChangeCompletedRef.current = false;
              const heading = pageHeadingRef.current;
              if (!heading) return;
              event.preventDefault();
              heading.focus();
              return;
            }
            const storedTrigger = statusReturnFocusRef.current;
            const remountedTrigger = statusReturnFocusIdRef.current
              ? document.getElementById(statusReturnFocusIdRef.current)
              : null;
            const trigger = storedTrigger?.isConnected
              ? storedTrigger
              : remountedTrigger instanceof HTMLButtonElement
                ? remountedTrigger
                : pageHeadingRef.current;
            if (!trigger) return;
            event.preventDefault();
            trigger.focus();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>
              {statusChangeRequest?.action === "approve"
                ? statusChangeRequest.source === "row"
                  ? "Approve this bill?"
                  : `Approve ${statusChangeRequest.billCount} selected bill${statusChangeRequest.billCount === 1 ? "" : "s"}?`
                : statusChangeRequest?.source === "row"
                  ? "Submit this bill for approval?"
                  : `Submit ${statusChangeRequest?.billCount ?? 0} selected bill${statusChangeRequest?.billCount === 1 ? "" : "s"} for approval?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {statusChangeRequest?.source === "row"
                ? statusChangeRequest.action === "approve"
                  ? "This approves the bill assigned to you or your current workflow step. The bill may become fully approved or move to its next approver."
                  : "The bill will be validated and sent to its assigned approver or routed by an advanced approval rule."
                : statusChangeRequest?.action === "approve"
                  ? "This approves each bill assigned to you or your current workflow step. A bill may become fully approved or move to its next approver. If any bill is no longer eligible, none are changed."
                  : "Every selected bill will be validated and sent to its assigned approver or routed by an advanced approval rule. If any bill has missing or invalid information, none are changed."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isStatusUpdating}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isStatusUpdating || !statusChangeRequest}
              onClick={(event) => {
                event.preventDefault();
                void handleStatusChange();
              }}
            >
              {isStatusUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating…
                </>
              ) : statusChangeRequest?.source === "row" ? (
                statusChangeRequest.action === "approve" ? (
                  "Approve bill"
                ) : (
                  "Submit bill"
                )
              ) : statusChangeRequest?.action === "approve" ? (
                `Approve ${statusChangeRequest.billCount} bill${statusChangeRequest.billCount === 1 ? "" : "s"}`
              ) : (
                `Submit ${statusChangeRequest?.billCount ?? 0} bill${statusChangeRequest?.billCount === 1 ? "" : "s"}`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={deleteRequest !== null}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setDeleteRequest(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Permanently delete {deleteRequest?.billIds.length ?? 0} bill
              {(deleteRequest?.billIds.length ?? 0) === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the selected bill records, documents,
              allocations, and AI suggestions. It cannot be undone. Email
              intake and audit evidence remain protected.
              {deleteRequest?.labels.length ? (
                <span className="mt-2 block font-medium text-foreground">
                  {deleteRequest.labels.slice(0, 5).join(", ")}
                  {deleteRequest.labels.length > 5
                    ? ` and ${deleteRequest.labels.length - 5} more`
                    : ""}
                </span>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={isDeleting || !deleteRequest}
              onClick={(event) => {
                event.preventDefault();
                void handleDeleteSelected();
              }}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting…
                </>
              ) : (
                "Delete permanently"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
